import { Component, inject, input, LOCALE_ID, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { PharmacyService } from '../../../../../services/pharmacy/pharmacy.service';
import { Error } from '../../../../public-user-component/auth/shared/error/error';
import { PharmacyRequestStatus } from '../../../../../pages/admin-dashboard/pharmacies/pharmacies.model';
import { LookupVTO } from '../../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { finalize } from 'rxjs';
import { ConfirmationClose } from './confirmation-close/confirmation-close';

@Component({
  selector: 'app-pharmacy-reason-dialog',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, Error, ConfirmationClose],
  templateUrl: './pharmacy-reason-dialog.html',
  styleUrl: './pharmacy-reason-dialog.scss',
})
export class PharmacyReasonDialog {
  type = input.required<'reject' | 'modify'>();
  private pharmacyService = inject(PharmacyService);
  private locale = inject(LOCALE_ID);
  isModal = signal<boolean>(false);
  pharmacyId = input.required<number>();
  title = input.required<string>();
  label = input.required<string>();
  placeholder = input<string>('Write your reason here');
  subtitle = input<string>('');
  confirmButtonText = input<string>('Save');
  reason = signal<string>('');
  loading = signal<boolean>(false);
  error = signal<boolean>(false);
  success = signal<boolean>(false);

  modificationForm = new FormGroup({
    comment: new FormControl('', {
      validators: [
        Validators.required,
        Validators.minLength(20),
        Validators.maxLength(4000),
        Validators.pattern(/^(?!\s*$).+/),
      ],
    }),
  });

  close = output();
  employeeRequestModification = output<{ action: LookupVTO }>();
  rejectionPharmacy = output<{ action: LookupVTO }>();

  ngOnInit() {
    this.modificationForm.get('comment')!.valueChanges.subscribe((value) => {
      this.reason.set(value || '');
    });
  }

  onOpenModal() {
    this.isModal.set(true);
  }
  onCloseModal() {
    this.isModal.set(false);
  }
  onCancel() {
    this.close.emit();
  }

  onConfirmRequestModification() {
    const { comment } = this.modificationForm.value;

    if (this.modificationForm.get('comment')?.errors) {
      return;
    }
    if (comment) {
      this.loading.set(true);

      this.pharmacyService
        .requestModification(this.pharmacyId(), { comment })
        .pipe(
          finalize(() => {
            this.loading.set(false);
          }),
        )
        .subscribe({
          next: () => {
            this.employeeRequestModification.emit({
              action: {
                id: PharmacyRequestStatus.Feedback,
                titleEn: 'Feedback Needed',
                titleAr: 'يحتاج إلى تعديل',
                profileImageFid: '',
              },
            });
            this.success.set(true);
            this.onCancel();
          },
          error: () => {
            this.error.set(true);
          },
        });
    }
  }

  onReject() {
    const { comment } = this.modificationForm.value;

    if (this.modificationForm.get('comment')?.errors) {
      return;
    }

    if (comment) {
      this.loading.set(true);

      this.pharmacyService
        .rejectPharmacy(this.pharmacyId(), { comment })
        .pipe(
          finalize(() => {
            this.loading.set(false);
          }),
        )
        .subscribe({
          next: () => {
            this.rejectionPharmacy.emit({
              action: {
                id: PharmacyRequestStatus.Rejected,
                titleEn: 'Rejected',
                titleAr: 'مرفوض',
                profileImageFid: '',
              },
            });
            this.success.set(true);
            this.onCancel();
          },
          error: () => {
            this.error.set(true);
          },
        });
    }
  }

  isArabic() {
    return this.locale.startsWith('ar');
  }

  isEnglish() {
    return this.locale.startsWith('en');
  }
}
